import Flex from "components/grid/Flex";
import { Button, CircularProgress, Dialog, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { grey } from "components/colors/Colors";
import React, { useState } from "react";
import utils from "utils/Utils";
import { useEnqueueError, useEnqueueSuccess } from "components/alert/SnackbarHooks";
import { useProcessLaboratoryFileWithAiMutation, useSaveAllMutation } from "scenes/patient/laboratory/LaboratoryApi";
import _ from "lodash";
import { useSelector } from "react-redux";

const LaboratoryAiProcessorDialog = ({ open, closeDialog, laboratoriesToProcess, patientId }) => {
    const { t: translate } = useTranslation();
    const isMobile = utils.checkMobileBrowser();
    const user = useSelector((state) => state.mainReducer.user);
    const [showShouldProcessQuestion, setShowShouldProcessQuestion] = useState(true);
    const [showShouldAcceptQuestion, setShowShouldAcceptQuestion] = useState(false);
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const enqueueError = useEnqueueError();
    const enqueueSuccess = useEnqueueSuccess();
    const [processLaboratoryFileWithAi] = useProcessLaboratoryFileWithAiMutation();
    const [saveAll] = useSaveAllMutation();
    const close = () => {
        setShowShouldProcessQuestion(true);
        setShowShouldAcceptQuestion(false);
        setIsLoading(false);
        setResults([]);
        closeDialog();
    };
    const processLaboratories = () => {
        setIsLoading(true);
        setShowShouldProcessQuestion(false);
        const promises = [];
        laboratoriesToProcess.forEach((lab) => {
            promises.push(
                processLaboratoryFileWithAi({ patientId, file: lab.file, careProviderId: user?.careProviderIds?.[0] })
                    .unwrap()
                    .then((result) => {
                        setResults((prevResults) => [...prevResults, result]);
                    }),
            );
        });
        Promise.allSettled(promises).then((promiseStatuses) => {
            console.log('all settled: ', promiseStatuses);
            setShowShouldProcessQuestion(false);
            setShowShouldAcceptQuestion(true);
            setIsLoading(false);
        });
    };
    const saveLaboratoryResults = () => {
        saveAll({ patientId, laboratories: results.flatMap((result) => result) })
            .then(() => {
                enqueueSuccess();
            })
            .catch(() => {
                enqueueError();
            })
            .finally(() => {
                close();
            });
    };
    return (
        <Dialog open={open} onClose={close} maxWidth={"lg"} fullWidth>
            <Flex item container column padding={20}>
                <Flex
                    id={"header"}
                    item
                    container
                    style={{ marginBottom: 5, borderBottom: `1px solid ${grey}` }}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                >
                    <Flex item container alignItems={"center"}>
                        <Typography
                            {...{
                                ...(isMobile ? { style: { fontWeight: "bold" } } : { variant: "h5" }),
                            }}
                        >
                            {translate("global.ai-processing")}
                        </Typography>
                    </Flex>
                </Flex>
                {showShouldProcessQuestion && (
                    <Flex item container column>
                        <Typography>
                            {translate("global.ai-processing-question")}
                        </Typography>
                        <Flex item container alignItems={"center"} style={{ marginTop: 15 }}>
                            <Button
                                variant={"contained"}
                                color={"primary"}
                                size={"small"}
                                onClick={close}
                                style={{ marginRight: 50 }}
                            >
                                {translate("global.no")}
                            </Button>
                            <Button
                                variant={"contained"}
                                color={"primary"}
                                size={"small"}
                                onClick={processLaboratories}
                            >
                                {translate("global.yes")}
                            </Button>
                        </Flex>
                    </Flex>
                )}

                {!showShouldProcessQuestion && isLoading &&
                    <Flex item container column>
                        <Typography>{translate('global.processing-progress')}</Typography>
                        <CircularProgress size={24} />
                    </Flex>}

                {showShouldAcceptQuestion && _.isEmpty(results) && !isLoading && (
                    <Flex item container column>
                        <Typography>{translate('global.ai-process-fail')}</Typography>

                        <Button variant={"contained"} color={"primary"} size={"small"} onClick={close}>
                            {translate("global.ok")}
                        </Button>
                    </Flex>
                )}

                {showShouldAcceptQuestion && !_.isEmpty(results) && (
                    <Flex item container column>
                        <Typography>
                            {translate("global.ai-process-success")}
                        </Typography>

                        {results.map((result, index) => {
                            return (
                                <Flex item column container key={index} style={{ marginBottom: 10 }}>
                                    {result.map((lab, index2) => (
                                        <Typography key={index2}>
                                            {lab.recordDate} - {lab.name}: {lab.labValue} {lab.unit}
                                        </Typography>
                                    ))}
                                </Flex>
                            );
                        })}
                        <Flex item container alignItems={"center"}>
                            <Button
                                variant={"contained"}
                                color={"secondary"}
                                size={"small"}
                                onClick={close}
                                style={{ marginRight: 50 }}
                            >
                                {translate("global.cancel")}
                            </Button>
                            <Button
                                variant={"contained"}
                                color={"primary"}
                                size={"small"}
                                onClick={saveLaboratoryResults}
                            >
                                {translate("global.accept")}
                            </Button>
                        </Flex>
                    </Flex>
                )}
            </Flex>
        </Dialog>
    );
};

export default LaboratoryAiProcessorDialog;
